import { ApiError } from '@/api/core/client';
import { getHashRoute } from '@/lib/urlRouting';
import { fetchTodoBootstrap } from '@/api/todoApi';
import { fetchNotesBootstrap } from '@/api/notesApi';
import { useAccountStore } from './accountStore';
import { useContactStore } from './contactStore';
import { useEmailStore } from './emailStore';
import { useFolderStore } from './folderStore';
import { useRealtimeStore } from './realtimeStore';
import { useRuleStore } from './ruleStore';
import { useSenderGroupingStore } from './senderGroupingStore';
import { useSignatureStore } from './signatureStore';
import { useUIStore } from './uiStore';
import { useKeyboardStore } from './keyboardStore';
import { loadAppSettings } from './appSettings';
import { useTodoStore } from './todoStore';
import { useCalendarStore } from './calendarStore';
import { useCalendarActionsStore } from './calendarActionsStore';
import { useNotesStore } from './notesStore';

declare global {
  interface Window {
    __accountStore__?: typeof useAccountStore;
  }
}

let bootstrapped = false;
let cleanupSubscriptions: Array<() => void> = [];

function cleanupStores() {
  if (!bootstrapped && cleanupSubscriptions.length === 0) {
    return;
  }

  cleanupSubscriptions.forEach((unsubscribe) => {
    try {
      unsubscribe();
    } catch (error) {
      console.error('Failed to unsubscribe store listener', error);
    }
  });
  cleanupSubscriptions = [];
  bootstrapped = false;

  if (typeof window !== 'undefined' && window.__accountStore__ === useAccountStore) {
    delete window.__accountStore__;
  }
}

/**
 * Bootstraps cross-store subscriptions.
 * Call once at startup to wire derived store behaviors.
 */
export function bootstrapStores(): () => void {
  if (bootstrapped) {
    return () => {};
  }

  bootstrapped = true;

  if (typeof window !== 'undefined') {
    window.__accountStore__ = useAccountStore;

    // Check if URL has a sender param - if so, set flag to prevent clearing sender group
    // during account/folder restoration from persistence
    const urlParams = new URLSearchParams(getHashRoute().search);
    const senderParam = urlParams.get('sender');
    if (senderParam) {
      useSenderGroupingStore.setState({ restoringFromUrl: true });
    }
  }

  cleanupSubscriptions = [];

  cleanupSubscriptions.push(
    useAccountStore.subscribe(
      (state) => state.selectedAccountId,
      (selectedAccountId) => {
        if (!selectedAccountId) {
          return;
        }
        const ruleStore = useRuleStore.getState();
        void ruleStore.setActiveAccount(selectedAccountId ?? null).catch((error) => {
          console.error('Failed to synchronize rules for account change', error);
        });
      },
      { fireImmediately: true }
    )
  );

  cleanupSubscriptions.push(
    useAccountStore.subscribe(
    (state) => state.isAuthenticated,
      (isAuthenticated) => {
      const signatureStore = useSignatureStore.getState();
      const todoStore = useTodoStore.getState();
      const calendarStore = useCalendarStore.getState();
      const calendarActionsStore = useCalendarActionsStore.getState();
      const notesStore = useNotesStore.getState();
      if (isAuthenticated) {
        void signatureStore.loadSignatures().catch((error) => {
          if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
            return;
          }
          console.error('Failed to load signatures after authentication', error);
        });
        void loadAppSettings().catch((error) => {
          console.error('Failed to load app settings after authentication', error);
        });
        todoStore.setLoading(true);
        void fetchTodoBootstrap()
          .then((payload) => {
            useTodoStore.getState().hydrate(payload);
          })
          .catch((error) => {
            console.error('Failed to load todo workspace', error);
          })
          .finally(() => {
            useTodoStore.getState().setLoading(false);
          });
        calendarStore.setLoading(true);
        void calendarActionsStore
          .bootstrap({ force: true })
          .catch((error) => {
            console.error('Failed to load calendar workspace', error);
            calendarStore.setError('Unable to load calendar data');
          })
          .finally(() => {
            calendarStore.setLoading(false);
          });
        notesStore.setLoading(true);
        void fetchNotesBootstrap()
          .then((payload) => {
            useNotesStore.getState().hydrate(payload);
          })
          .catch((error) => {
            console.error('Failed to load notes workspace', error);
            useNotesStore.getState().setBootstrapFailed(true);
          })
          .finally(() => {
            useNotesStore.getState().setLoading(false);
          });
      } else {
        signatureStore.clearSignatures();
        useContactStore.getState().clearContacts();
        useRuleStore.getState().clear();
        useKeyboardStore.getState().reset();
        todoStore.reset();
        calendarStore.reset();
        notesStore.reset();
      }
    },
    { fireImmediately: true }
  )
  );

  cleanupSubscriptions.push(
    useAccountStore.subscribe(
      (state) => ({ accountsLoading: state.accountsLoading, selectedAccountId: state.selectedAccountId }),
      (current, previous) => {
        // Load folders when accounts finish loading and we have a selected account
        if (previous.accountsLoading && !current.accountsLoading && current.selectedAccountId) {
          useFolderStore
            .getState()
            .loadFolders(current.selectedAccountId)
            .catch((error) => {
              console.error('Failed to load folders after accounts loaded', error);
            });
          // ALSO warm every other account's folder cache in the background:
          // the unified sidebar rollup needs every account's counts, and a
          // counts event for a never-visited account had no cache to land in
          // (E8). Failures are non-fatal (retried on demand).
          const actual = useAccountStore.getState().actualAccounts();
          actual.forEach((account) => {
            if (account.id !== current.selectedAccountId) {
              // background: cache warming must not toggle the global
              // foldersLoading — that flickered realtimeEnabled and churned
              // the SSE connection on every app start (N14).
              void useFolderStore
                .getState()
                .loadFolders(account.id, { background: true })
                .catch(() => undefined);
            }
          });
          return;
        }

        // Handle account switching
        if (current.selectedAccountId !== previous.selectedAccountId) {
          if (!current.selectedAccountId) {
            useFolderStore.getState().clearFolders();
            return;
          }

          // Clear sender grouping state when switching accounts
          // BUT skip if we're restoring a sender group from URL
          const senderGroupingStore = useSenderGroupingStore.getState();
          if (!senderGroupingStore.restoringFromUrl) {
            senderGroupingStore.resetSenderGroupingState('account switch');
          }

          // Invalidate email cache for the previous account to prevent stale data
          if (previous.selectedAccountId) {
            useEmailStore.getState().invalidateCacheForAccount(previous.selectedAccountId);
          }

          useFolderStore
            .getState()
            .loadFolders(current.selectedAccountId)
            .catch((error) => {
              console.error('Failed to load folders for account change', error);
            });
        }
      },
      { equalityFn: (a, b) => a.accountsLoading === b.accountsLoading && a.selectedAccountId === b.selectedAccountId }
    )
  );

  cleanupSubscriptions.push(
    useFolderStore.subscribe(
      (state) => {
        const accountId = useAccountStore.getState().selectedAccountId;
        if (!accountId) {
          return { accountId: null as string | null, folderId: null as string | null };
        }
        return {
          accountId,
          folderId: state.selectedFolderByAccount[accountId] ?? null,
        };
      },
      (current, previous) => {
        if (!current?.accountId || !current.folderId) {
          return;
        }
        if (
          previous &&
          previous.accountId === current.accountId &&
          previous.folderId === current.folderId
        ) {
          return;
        }

        useRealtimeStore.getState().cancelScheduledReload();

        // Clear sender grouping state when switching folders
        // BUT skip if we're restoring a sender group from URL
        const senderGroupingStore = useSenderGroupingStore.getState();
        if (!senderGroupingStore.restoringFromUrl) {
          senderGroupingStore.resetSenderGroupingState('folder switch');
        }

        useUIStore.getState().resetEmailSelection();
        useEmailStore.getState().clearEmails();
      },
      { fireImmediately: false }
    )
  );

  (() => {
    let previousLoading = useEmailStore.getState().emailsLoading;
    cleanupSubscriptions.push(
      useEmailStore.subscribe(
        (state) => state.emailsLoading,
        (emailsLoading) => {
          if (previousLoading && !emailsLoading) {
            void useSenderGroupingStore.getState().flushPendingMailboxReload();
          }
          previousLoading = emailsLoading;
        },
      )
    );
  })();

  // Session 6+: Realtime event handling

  return cleanupStores;
}

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    cleanupStores();
  });
}
